import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import React from "react";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";
import { workoutApi } from "@/api/workoutApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { AxiosError } from "axios";
import { useValidationErrors } from "@/hooks/useValidationErrors";

interface WorkoutTitleModalProps {
  open: boolean;
  onClose: () => void;
  workoutNanoId: string;
  initialTitle: string;
  onSuccess?: (newTitle: string) => void;
}

/**
 * WorkoutTitleModal — change workout title
 */
export const WorkoutTitleModal: React.FC<WorkoutTitleModalProps> = ({
  open,
  onClose,
  workoutNanoId,
  initialTitle,
  onSuccess,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const {
    validationErrors,
    removeValidationError,
    clearAllValidationErrors,
    setValidationErrorsFromResponse,
  } = useValidationErrors();
  const invalidate = useInvalidateResources();
  const { handleSuccess, handleError } = useToastHandler();

  useEffect(() => {
    setTitle(initialTitle);
    clearAllValidationErrors(); // Clear validation errors when modal opens
  }, [initialTitle, open, clearAllValidationErrors]);

  const mutation = useMutation({
    mutationFn: async () => {
      return await workoutApi.updateWorkoutTitle(workoutNanoId, title.trim());
    },
    onSuccess: (response) => {
      handleSuccess("Workout title updated successfully", 1000);
      // Clear validation errors on success
      clearAllValidationErrors();
      invalidate([
        ["/api/workouts", response.workout.claimed_train_local_date],
      ]);
      onSuccess?.(title.trim());
      onClose();
    },
    onError: (error) => {
      if (
        error instanceof AxiosError &&
        error.response?.data?.validation_errors
      ) {
        // Store validation errors to display under inputs
        setValidationErrorsFromResponse(error.response.data.validation_errors);
        //return; // Don't show generic error toast for validation errors
      }
      handleError(error);
    },
  });

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      style={{
        backdropFilter: "blur(4px)",
        backgroundColor: "rgba(13, 28, 24, 0.85)",
        zIndex: 9999, // Use a high z-index value to ensure it appears above the BottomNavbar
      }}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-tx-dark">
            Change Workout Title
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-3 text-tx-default">
          <input
            type="text"
            className={`w-full p-2 border rounded text-tx-dark dark:bg-gray-700 dark:text-tx-default ${
              validationErrors.title ? "border-red-500" : ""
            }`}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              // Clear validation errors when user starts typing
              removeValidationError("title");
            }}
            placeholder="E.g. Recovery leg day"
            disabled={mutation.status === "pending"}
            maxLength={64}
            autoFocus
          />
          {validationErrors.title && (
            <div className="mt-1">
              {validationErrors.title.map((error, index) => (
                <p key={index} className="text-sm text-tx-error">
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            variant="primary"
            size="lg"
            className="w-full py-6"
            disabled={
              mutation.status === "pending" ||
              !title.trim() ||
              title === initialTitle
            }
            onClick={() => {
              if (!title.trim() || title === initialTitle) return;
              mutation.mutate();
            }}
          >
            {mutation.status === "pending" ? "Saving..." : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutTitleModal;
